import { useMemo } from "react";

import type { BoardColumn } from "~/shared/types/quote-board-types.ts";
import type { GridPrefColumn } from "~/shared/utils/apply-grid-prefs.ts";
import { applyGridPrefs } from "~/shared/utils/apply-grid-prefs.ts";
import { getDefaultQuoteBoardGridPrefs } from "~/shared/utils/default-grid-prefs.ts";
import { GridIdentifiers } from "~/shared/utils/grid-identifier-enums.ts";

import { getQuoteBoardColumns } from "../constants/board-columns.tsx";

type UseQuoteBoardColumnsArgs = {
	isLoading: boolean;
	isInternal: boolean;
	quoteType?: string;
	isBulkTenderAvailable?: boolean;
	/** The user's saved Quote Board column prefs (external users only). */
	columnPrefs?: GridPrefColumn[];
	onOpenCarrierSelection?: (quoteId: number) => void;
	onOpenBulkCarrierSelection?: (quoteId: number) => void;
	onOpenInsurance?: (quoteId: number) => void;
	hasInsurance?: (quoteId: number) => boolean;
};

/**
 * Builds the Quote Board grid columns by merging the code-defined defaults
 * with the user's persisted column preferences (visibility, order, width).
 *
 * Mirrors `useLoadColumns` (Shipment Board). The trailing action column (the
 * field-less requote button) is pinned to the END of the grid: `applyGridPrefs`
 * pins all field-less columns to the FRONT, so it is excluded from the prefs
 * pass and re-appended afterward as a non-reorderable column.
 */
export const useQuoteBoardColumns = ({
	isLoading,
	isInternal,
	quoteType = "Individual",
	isBulkTenderAvailable = false,
	columnPrefs,
	onOpenCarrierSelection,
	onOpenBulkCarrierSelection,
	onOpenInsurance,
	hasInsurance,
}: UseQuoteBoardColumnsArgs): BoardColumn[] => {
	return useMemo(() => {
		const allColumns = getQuoteBoardColumns(
			isLoading,
			isInternal,
			onOpenCarrierSelection,
			onOpenInsurance,
			hasInsurance,
			onOpenBulkCarrierSelection,
			quoteType,
			isBulkTenderAvailable,
		);
		const base = isInternal
			? allColumns
			: allColumns.filter((col) => !col.hiddenFromCustomers);

		const trailingColumns = base.filter((col) => !col.field);
		const reorderableColumns = base.filter((col) => col.field);

		const defaults =
			getDefaultQuoteBoardGridPrefs(isInternal, quoteType)[
				GridIdentifiers["Quote Board"]
			]?.columns ?? [];
		const defaultsByField = new Map(
			defaults.map((pref) => [pref.columnId, pref]),
		);
		const savedByField = new Map(
			(columnPrefs ?? []).map((pref) => [pref.columnId, pref]),
		);

		// Merge defaults with the user's saved prefs. `isVisible`, `widthSize`,
		// and `ordinalPosition` are user preferences and the saved value wins.
		// `visibility` is column metadata (which deployments may see a column at
		// all) — defaults always win so updates to a column's deployment
		// visibility take effect for users with stale persisted prefs.
		const mergedById = new Map<string, GridPrefColumn>();
		for (const def of defaults) {
			const saved = savedByField.get(def.columnId);
			mergedById.set(
				def.columnId,
				saved ? { ...saved, visibility: def.visibility } : def,
			);
		}
		// Preserve any saved entries not present in defaults (forward compat).
		for (const saved of savedByField.values()) {
			if (!defaultsByField.has(saved.columnId)) {
				mergedById.set(saved.columnId, saved);
			}
		}
		const mergedPrefs = [...mergedById.values()];

		const applied = applyGridPrefs(reorderableColumns, mergedPrefs, isInternal);

		const trailing = trailingColumns.map((col, index) => ({
			...col,
			show: true,
			reorderable: false,
			orderIndex: applied.length + index,
		}));

		return [...applied, ...trailing];
	}, [
		isLoading,
		isInternal,
		quoteType,
		isBulkTenderAvailable,
		columnPrefs,
		onOpenCarrierSelection,
		onOpenBulkCarrierSelection,
		onOpenInsurance,
		hasInsurance,
	]);
};
